import { axios } from '@/services/axios';
import {
    Box,
    HStack,
    Skeleton,
    SkeletonCircle,
    Stack,
} from '@chakra-ui/react';
import { useQuery } from 'hooks/useCustomQuery';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    LoadingIndicator,
} from 'components/v4';
import FormBase from '@/components/forms/new/FormBase';
import { isEmpty } from 'lodash';
import { useDefinedAttributes } from 'pages/services/serviceprofiles/vendorDefined/DefinedAttributesContext';
import DefinedAttributes from 'pages/services/serviceprofiles/vendorDefined/attributes/definedAttributes';
import useDefinedAttributeValidation from 'pages/services/serviceprofiles/vendorDefined/attributes/useDefinedAttributeValidation';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import BreadcrumbTrail from 'components/BreadcrumbTrail';

// WORK IN PROGRESS
// COME BACK HERE ONCE SERVICE PROFILE CARD UI IS DISPLAYED ON DASHBOARD SERVICES
export default function EditVendorDefined() {
    const { id: serviceId } = useParams();
    const { requiredScope } = useSelector((state) => state.login);
    const navigate = useNavigate();

    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const handleBackButton = () => navigate('/dashboard-services');

    const { dispatch, attributesList, attributesErrors } = useDefinedAttributes();
    const { handleAttributeError, removeAttributeError } = useDefinedAttributeValidation();

    // get company service
    const { data: serviceData, isFetching: serviceLoading } = useQuery({
        queryKey: ['service', serviceId],
        queryFn: async () => {
            const res = await axios.get(`service/${serviceId}`);
            return res.data;
        },
        enabled: Boolean(serviceId),
        onSuccess: (data) => {
            // attributes list data
            dispatch({
                type: 'UPDATE_ATT_LIST',
                payload: data.attributeList,
            });
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message);
            handleBackButton();
        },
        refetchOnWindowFocus: false,
        cacheTime: 0,
        staleTime: 0,
    });

    const handleSubmit = async () => {
        console.log('handleSubmit called'); // Debug log
        setError(null);
        let hasError = false;

        // check for any attributes errors
        if (attributesErrors.length > 0) {
            console.log('Attributes errors found:', attributesErrors); // Debug log
            hasError = true;
        }

        // check for required attributes
        if (attributesList?.length > 0) {
            attributesList.forEach((att, index) => {
                const validation = JSON.parse(
                    att.definedAttributeValidationType,
                );

                // if required, input cannot be empty
                if (validation.Required && isEmpty(att.value)) {
                    handleAttributeError(index, 'Field is required.');
                    hasError = true;
                } else {
                    removeAttributeError(index);
                }
            });
        }
        if (hasError) {
            console.log('Validation errors found, aborting submit'); // Debug log
            return;
        }

        console.log('Starting API call with data:', { // Debug log
            id: serviceData.id,
            name: serviceData.name,
            serviceDefinitionID: serviceData.serviceDefinitionID,
            attributeList: attributesList,
        });

        setIsLoading(true);
        try {
            const response = await axios.put('service/vendordefined', {
                id: serviceData.id,
                name: serviceData.name,
                serviceDefinitionID: serviceData.serviceDefinitionID,
                attributeList: attributesList,
            });

            console.log('API call successful:', response); // Debug log
            toast.success('Successfully updated a Vendor Defined Service!');
            // navigate to dashboard services
            handleBackButton();
        } catch (e) {
            console.error('API call failed:', e); // Debug log
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    const breadCrumbTrail = [
        { label: 'Dashboard', path: '/dashboard-services' },
        { label: 'Services', path: '' },
    ];

    return (
        <>
            <BreadcrumbTrail breadCrumbTrail={breadCrumbTrail} handleBackButton={handleBackButton} />
            <FormBase
                header={`Edit ${serviceData?.name || 'Vendor Defined Service'}`}
                subTitleHeader={true}
                backLink="/dashboard-services"
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                finalFormSubmit={handleSubmit}
                saveButtonText="Update"
                isDisabled={attributesList?.length === 0}
                initialValues={{}}
            >
                {() => (
                    <>
                        {error && (
                            <Alert status="error" sx={{ marginTop: '1rem' }}>
                                <AlertIcon />
                                <AlertDescription>
                                    {error?.response?.data?.message ||
                                        'An error occurred. Please try again later.'}
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Dynamic Attributes */}
                        <Box>
                            {serviceLoading ? (
                                <Box>
                                    {[...Array(3)].map((_, index) => (
                                        <Stack key={index} mb={3}>
                                            <HStack>
                                                <Skeleton h={5} w="25%" />
                                                <SkeletonCircle h={5} w={5} />
                                            </HStack>
                                            <Skeleton h={10} borderRadius={4} />
                                            <Skeleton h={5} w="15%" />
                                        </Stack>
                                    ))}
                                </Box>
                            ) : attributesList?.length > 0 ? (
                                <DefinedAttributes requiredScope={requiredScope} />
                            ) : (
                                <Alert status="info">
                                    <AlertIcon />
                                    No information is required at this time!
                                </Alert>
                            )}
                        </Box>
                    </>
                )}
            </FormBase>
        </>
    );
}
